import React, { useState, useEffect, useRef } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOutlinedIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Dialog from "@mui/material/Dialog";
import MDBadge from "components/MDBadge";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { ToastContainer, toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import Select from "react-select";
import Papa from "papaparse";
import colors from "assets/theme/base/colors";
import "react-toastify/dist/ReactToastify.css";
import "../campus/Campus.css";
import { hasAdmissionPermission } from "hooks/permissions";

const { info } = colors;

const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

const FeeStructureV2 = () => {
  // State variables
  const [campaigns, setCampaigns] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [feeStructures, setFeeStructures] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [totalFee, setTotalFee] = useState("");
  const [showAddFeeModal, setShowAddFeeModal] = useState(false);
  const [showUpdateFeeModal, setShowUpdateFeeModal] = useState(false);
  const [showViewFeeModal, setShowViewFeeModal] = useState(false);
  const [showDeleteFeeModal, setShowDeleteFeeModal] = useState(false);
  const [currentFeeStructureId, setCurrentFeeStructureId] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedFeeStructure, setSelectedFeeStructure] = useState(null);
  const [formErrors, setFormErrors] = useState({
    campaign: "",
    program: "",
    totalFee: "",
  });
  const [filteredFeeStructures, setFilteredFeeStructures] = useState([]);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const csvInputRef = useRef(null);

  // Fetch campaigns
  const getCampaigns = async () => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    try {
      const response = await fetch(
        `${BASE_URL}/campaign/get_campaigns`,
        requestOptions
      );
      console.log("Campaigns response status:", response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Campaigns data received:", data);
      setCampaigns(data.campaigns || []);
    } catch (error) {
      console.error("Fetch campaigns error:", error);
      toast.error("Failed to fetch campaigns");
    }
  };

  // Fetch degrees in campaign
  const getDegreesInCampaign = async (campaignId) => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    try {
      const response = await fetch(
        `${BASE_URL}/campaign/get_degrees_in_campaign/${campaignId}`,
        requestOptions
      );
      console.log("Degrees response status:", response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Degrees data received:", data);
      setDegrees(data.degrees || []);

      // Extract programs from degrees
      const allPrograms = [];
      data.degrees?.forEach((degree) => {
        if (degree.degree_name) {
          allPrograms.push({
            id: degree.degree_id,
            program_name: degree.degree_name,
          });
        }
      });
      setPrograms(allPrograms);
    } catch (error) {
      console.error("Fetch degrees error:", error);
      toast.error("Failed to fetch degrees");
      setDegrees([]);
      setPrograms([]);
    }
  };

  // Fetch fee structures
  const getFeeStructures = async (campaignId) => {
    if (!campaignId) return;

    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    try {
      const response = await fetch(
        `${BASE_URL}/campaign/get_fee_structure/${campaignId}`,
        requestOptions
      );
      console.log("Fee structures response status:", response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Fee structures data received:", data);
      setFeeStructures(data.fee_structures || []);
      setFilteredFeeStructures(data.fee_structures || []);
    } catch (error) {
      console.error("Fetch fee structures error:", error);
      toast.error("Failed to fetch fee structures");
      setFeeStructures([]);
      setFilteredFeeStructures([]);
    }
  };

  // Add fee structure
  const addFeeStructure = async () => {
    if (!validateFeeForm()) return;

    const feeData = {
      campaign_id: selectedCampaign.value,
      program_id: selectedProgram.value,
      total_fee: parseFloat(totalFee),
    };

    try {
      const response = await fetch(`${BASE_URL}/campaign/add_fee_structure`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(feeData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add fee structure");
      }

      const newFeeStructure = await response.json();
      toast.success("Fee structure added successfully");
      getFeeStructures(selectedCampaign.value);
      resetForm();
    } catch (error) {
      console.error("Error adding fee structure:", error);
      toast.error(error.message || "Failed to add fee structure");
    } finally {
      setShowAddFeeModal(false);
    }
  };

  // Update fee structure
  const updateFeeStructure = async () => {
    if (!validateFeeForm()) return;

    const feeData = {
      id: currentFeeStructureId,
      campaign_id: selectedCampaign.value,
      program_id: selectedProgram.value,
      total_fee: parseFloat(totalFee),
    };

    try {
      const response = await fetch(
        `${BASE_URL}/campaign/update_fee_structure`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(feeData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update fee structure");
      }

      toast.success("Fee structure updated successfully");
      getFeeStructures(selectedCampaign.value);
      resetForm();
    } catch (error) {
      console.error("Error updating fee structure:", error);
      toast.error(error.message || "Failed to update fee structure");
    } finally {
      setShowUpdateFeeModal(false);
    }
  };

  // Delete fee structure
  const deleteFeeStructure = async () => {
    try {
      console.log("Deleting fee structure with ID:", currentFeeStructureId);
      const response = await fetch(
        `${BASE_URL}/campaign/delete_fee_structure/${currentFeeStructureId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Delete response status:", response.status);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete fee structure");
      }

      toast.success("Fee structure deleted successfully");
      getFeeStructures(selectedCampaign.value);
    } catch (error) {
      console.error("Error deleting fee structure:", error);
      toast.error(error.message || "Failed to delete fee structure");
    } finally {
      setShowDeleteFeeModal(false);
    }
  };

  // Download sample CSV with all programs in selected campaign
  const downloadSampleCsv = async () => {
    if (!selectedCampaign) {
      toast.error("Please select a campaign first");
      return;
    }

    try {
      const response = await fetch(
        `${BASE_URL}/campaign/download_fee_structure_sample/${selectedCampaign.value}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || "Failed to download sample CSV"
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fee_structure_sample_${selectedCampaign.label || "campaign"}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Sample CSV downloaded. Fill total_fee and upload.");
    } catch (error) {
      console.error("Download sample CSV error:", error);
      toast.error(error.message || "Failed to download sample CSV");
    }
  };

  // Parse fee structure CSV
  const parseFeeStructureCsv = (file) => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) =>
          header.trim().replace(/^"|"$/g, "").toLowerCase(),
        complete: (results) => {
          try {
            const headers = results.meta.fields || [];
            const requiredHeaders = ["program_id", "total_fee"];
            const missingHeaders = requiredHeaders.filter(
              (h) => !headers.includes(h)
            );
            if (missingHeaders.length > 0) {
              reject(`Missing required columns: ${missingHeaders.join(", ")}`);
              return;
            }

            if (!results.data?.length) {
              reject("CSV must include at least one data row");
              return;
            }

            const data = [];
            const errors = [];

            results.data.forEach((row, index) => {
              const programId = (row.program_id || "").toString().trim();
              const totalFee = (row.total_fee || "").toString().trim();

              if (!programId) {
                errors.push(`Row ${index + 2}: program_id is required`);
                return;
              }

              if (totalFee !== "" && isNaN(Number.parseFloat(totalFee))) {
                errors.push(`Row ${index + 2}: total_fee must be a valid number`);
                return;
              }

              data.push({
                program_id: programId,
                total_fee: totalFee,
              });
            });

            resolve({ data, errors });
          } catch (error) {
            reject("Error parsing CSV file");
          }
        },
        error: () => reject("Error reading CSV file"),
      });
    });
  };

  // Upload filled fee structure CSV
  const handleCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    // Reset so same file can be uploaded again
    event.target.value = "";

    if (!file) return;

    if (!selectedCampaign) {
      toast.error("Please select a campaign first");
      return;
    }

    if (file.type !== "text/csv" && !file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please upload a CSV file");
      return;
    }

    setUploadingCsv(true);
    try {
      const { data, errors } = await parseFeeStructureCsv(file);

      if (errors.length > 0) {
        toast.error(errors.slice(0, 3).join(" | "));
        return;
      }

      if (!data.length) {
        toast.error("No valid rows found in CSV");
        return;
      }

      const response = await fetch(
        `${BASE_URL}/campaign/bulk_upload_fee_structure`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            campaign_id: selectedCampaign.value,
            fees: data,
          }),
        }
      );

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || "Failed to upload fee structures");
      }

      const parts = [];
      if (result.created) parts.push(`${result.created} created`);
      if (result.updated) parts.push(`${result.updated} updated`);
      if (result.skipped) parts.push(`${result.skipped} skipped`);
      toast.success(
        parts.length
          ? `Fee structures uploaded: ${parts.join(", ")}`
          : "Fee structures processed successfully"
      );

      if (result.errors?.length) {
        toast.warn(
          `${result.errors.length} row(s) had issues. First: ${
            result.errors[0].error
          }`
        );
      }

      getFeeStructures(selectedCampaign.value);
    } catch (error) {
      console.error("Upload fee structure CSV error:", error);
      toast.error(error.message || "Failed to upload fee structures");
    } finally {
      setUploadingCsv(false);
    }
  };

  // View fee structure
  const viewFeeStructure = (id) => {
    const feeStructure = feeStructures.find((fs) => fs.id === id);
    setSelectedFeeStructure(feeStructure);
    setShowViewFeeModal(true);
  };

  // Search functionality
  const handleSearchKeywordChange = (e) => {
    const searchTerm = e.target.value.trim().toLowerCase();
    setSearchKeyword(searchTerm);

    if (searchTerm === "") {
      setFilteredFeeStructures(feeStructures);
    } else {
      const filtered = feeStructures.filter((feeStructure) => {
        const programMatch =
          feeStructure.program_name &&
          feeStructure.program_name.toLowerCase().includes(searchTerm);
        const degreeMatch =
          feeStructure.degree_name &&
          feeStructure.degree_name.toLowerCase().includes(searchTerm);
        const feeMatch =
          feeStructure.total_fee &&
          feeStructure.total_fee.toString().includes(searchTerm);
        return programMatch || degreeMatch || feeMatch;
      });
      setFilteredFeeStructures(filtered);
    }
  };

  // Form validation
  const validateFeeForm = () => {
    const errors = {};

    if (!selectedCampaign) {
      errors.campaign = " * Campaign is required";
    }
    if (!selectedProgram) {
      errors.program = " * Program is required";
    }
    if (!totalFee || totalFee.trim() === "") {
      errors.totalFee = " * Total fee is required";
    } else if (isNaN(totalFee) || parseFloat(totalFee) <= 0) {
      errors.totalFee = " * Total fee must be a positive number";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle focus to clear errors
  const handleFocus = (fieldName) => {
    setFormErrors((prevFormErrors) => ({
      ...prevFormErrors,
      [fieldName]: "",
    }));
  };

  // Reset form
  const resetForm = () => {
    setSelectedProgram(null);
    setTotalFee("");
    setFormErrors({
      campaign: "",
      program: "",
      totalFee: "",
    });
  };

  // Handle campaign selection
  const handleCampaignChange = (selectedOption) => {
    setSelectedCampaign(selectedOption);
    if (selectedOption) {
      getDegreesInCampaign(selectedOption.value);
      getFeeStructures(selectedOption.value);
    } else {
      setDegrees([]);
      setPrograms([]);
      setFeeStructures([]);
      setFilteredFeeStructures([]);
    }
    setSelectedProgram(null);
    handleFocus("campaign");
  };

  // Dialog handlers
  const showFeeAddModal = () => {
    resetForm();
    setShowAddFeeModal(true);
  };

  const showFeeUpdateModal = (id) => {
    const feeStructure = feeStructures.find((fs) => fs.id === id);
    if (feeStructure) {
      setCurrentFeeStructureId(id);
      setSelectedProgram({
        value: feeStructure.program_id,
        label: feeStructure.program_name,
      });
      setTotalFee(feeStructure.total_fee.toString());
      setShowUpdateFeeModal(true);
    }
  };

  const showFeeDeleteModal = (id) => {
    setCurrentFeeStructureId(id);
    setShowDeleteFeeModal(true);
  };

  // Initial render
  useEffect(() => {
    getCampaigns();
  }, []);

  // Prepare options for react-select
  const campaignOptions = campaigns.map((campaign) => ({
    value: campaign.id,
    label: campaign.name,
  }));

  const programOptions = programs.map((program) => ({
    value: program.id,
    label: `${program.program_name}`,
  }));

  // Table columns
  const columns = [
    { Header: "Sr.No", accessor: "serial", align: "center" },
    { Header: "Program Name", accessor: "program_name", align: "center" },
    { Header: "Total Fee", accessor: "total_fee", align: "center" },
    { Header: "Actions", accessor: "actions", align: "center" },
  ];

  // Table rows
  const rows = filteredFeeStructures.map((feeStructure, index) => ({
    serial: index + 1,
    program_name: feeStructure.program_name || "N/A",
    total_fee: `PKR ${feeStructure.total_fee?.toLocaleString() || "0"}`,
    actions: (
      <>
        {hasAdmissionPermission("FeeStructure", "UPDATE") && (
          <IconButton onClick={() => showFeeUpdateModal(feeStructure.id)}>
            <EditIcon className="icon-button-edit" />
          </IconButton>
        )}
        {hasAdmissionPermission("FeeStructure", "READ") && (
          <IconButton onClick={() => viewFeeStructure(feeStructure.id)}>
            <VisibilityOutlinedIcon className="icon-button-view" />
          </IconButton>
        )}
        {hasAdmissionPermission("FeeStructure", "DELETE") && (
          <IconButton onClick={() => showFeeDeleteModal(feeStructure.id)}>
            <DeleteIcon className="icon-button-delete" />
          </IconButton>
        )}
      </>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ToastContainer />
      <MDBox pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card className="mt-5 card1">
              <MDBox
                mx={0}
                mt={0}
                py={2}
                px={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "20px" }}
                >
                  <div style={{ minWidth: "300px" }}>
                    <Select
                      options={campaignOptions}
                      value={selectedCampaign}
                      onChange={handleCampaignChange}
                      placeholder="Select Campaign"
                      isClearable
                      menuPortalTarget={document.body} // <-- renders dropdown at body level
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: "40px",
                          fontSize: "14px",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999, // make sure it's above other elements
                        }),
                      }}
                    />
                  </div>
                  <Paper component="form" className="search-paper">
                    <InputBase
                      sx={{ ml: 1, flex: 1, fontSize: "16px" }}
                      placeholder="Search"
                      inputProps={{ "aria-label": "search fee structure" }}
                      value={searchKeyword}
                      onChange={handleSearchKeywordChange}
                    />
                    <SearchIcon className="search-icon" />
                  </Paper>
                </div>
                {hasAdmissionPermission("FeeStructure", "CREATE") && (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <Button
                      variant="filled"
                      startIcon={<DownloadIcon />}
                      onClick={downloadSampleCsv}
                      className="add-button"
                      disabled={!selectedCampaign}
                    >
                      Download Sample
                    </Button>
                    <input
                      ref={csvInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      style={{ display: "none" }}
                      onChange={handleCsvUpload}
                    />
                    <Button
                      variant="filled"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => {
                        if (!selectedCampaign) {
                          toast.error("Please select a campaign first");
                          return;
                        }
                        csvInputRef.current?.click();
                      }}
                      className="add-button"
                      disabled={!selectedCampaign || uploadingCsv}
                    >
                      {uploadingCsv ? "Uploading..." : "Upload CSV"}
                    </Button>
                    <Button
                      variant="filled"
                      endIcon={<AddIcon className="add-icon" />}
                      onClick={showFeeAddModal}
                      className="add-button"
                    >
                      Add
                    </Button>
                  </div>
                )}
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Add Fee Structure Dialog */}
      <Dialog
        open={showAddFeeModal}
        onClose={() => setShowAddFeeModal(false)}
        aria-labelledby="responsive-dialog-title"
        maxWidth="lg"
        PaperProps={{
          className: "dialog-paper",
        }}
      >
        <DialogTitle id="responsive-dialog-title" className="dialog-title">
          {"Add Fee Structure"}
        </DialogTitle>
        <DialogContent className="dialog-content">
          <div className="row">
            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Campaign</label>
                <Select
                  options={campaignOptions}
                  value={selectedCampaign}
                  onChange={handleCampaignChange}
                  placeholder="Select Campaign"
                  isClearable
                  onFocus={() => handleFocus("campaign")}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "48px",
                      fontSize: "16px",
                    }),
                  }}
                />
              </div>
              <div className="error-message1">{formErrors.campaign}</div>
            </div>

            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Program</label>
                <Select
                  options={programOptions}
                  value={selectedProgram}
                  onChange={(selectedOption) => {
                    setSelectedProgram(selectedOption);
                    handleFocus("program");
                  }}
                  placeholder="Select Program"
                  isClearable
                  onFocus={() => handleFocus("program")}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "48px",
                      fontSize: "16px",
                    }),
                  }}
                />
              </div>
              <div className="error-message1">{formErrors.program}</div>
            </div>

            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Total Fee</label>
                <input
                  name="total_fee"
                  type="number"
                  step="0.01"
                  min="0"
                  value={totalFee}
                  onChange={(e) => setTotalFee(e.target.value)}
                  onFocus={() => handleFocus("totalFee")}
                  className="form-control form-control-lg"
                  placeholder="Enter total fee"
                />
              </div>
              <div className="error-message1">{formErrors.totalFee}</div>
            </div>
          </div>
        </DialogContent>
        <DialogActions style={{ marginRight: "20px" }}>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowAddFeeModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="dialog-actions-button positive"
            onClick={addFeeStructure}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Fee Structure Dialog */}
      <Dialog
        open={showUpdateFeeModal}
        onClose={() => setShowUpdateFeeModal(false)}
        aria-labelledby="responsive-dialog-title"
        maxWidth="lg"
        PaperProps={{
          className: "dialog-paper",
        }}
      >
        <DialogTitle id="responsive-dialog-title" className="dialog-title">
          {"Update Fee Structure"}
        </DialogTitle>
        <DialogContent className="dialog-content">
          <div className="row">
            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Campaign</label>
                <Select
                  options={campaignOptions}
                  value={selectedCampaign}
                  onChange={handleCampaignChange}
                  placeholder="Select Campaign"
                  isClearable
                  onFocus={() => handleFocus("campaign")}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "48px",
                      fontSize: "16px",
                    }),
                  }}
                />
              </div>
              <div className="error-message1">{formErrors.campaign}</div>
            </div>

            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Program</label>
                <Select
                  options={programOptions}
                  value={selectedProgram}
                  onChange={(selectedOption) => {
                    setSelectedProgram(selectedOption);
                    handleFocus("program");
                  }}
                  placeholder="Select Program"
                  isClearable
                  onFocus={() => handleFocus("program")}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "48px",
                      fontSize: "16px",
                    }),
                  }}
                />
              </div>
              <div className="error-message1">{formErrors.program}</div>
            </div>

            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Total Fee</label>
                <input
                  name="total_fee"
                  type="number"
                  step="0.01"
                  min="0"
                  value={totalFee}
                  onChange={(e) => setTotalFee(e.target.value)}
                  onFocus={() => handleFocus("totalFee")}
                  className="form-control form-control-lg"
                  placeholder="Enter total fee"
                />
              </div>
              <div className="error-message1">{formErrors.totalFee}</div>
            </div>
          </div>
        </DialogContent>
        <DialogActions style={{ marginRight: "20px" }}>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowUpdateFeeModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="dialog-actions-button positive"
            onClick={updateFeeStructure}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Fee Structure Dialog */}
      <Dialog
        open={showDeleteFeeModal}
        onClose={() => setShowDeleteFeeModal(false)}
        PaperProps={{
          sx: {
            width: "450px",
            borderRadius: "20px",
            opacity: 1,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle id="responsive-dialog-title" className="dialog-delete">
          Delete Fee Structure
        </DialogTitle>
        <DialogContent className="content-delete">
          Are you sure you want to delete this fee structure?
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowDeleteFeeModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="dialog-actions-button positive"
            onClick={deleteFeeStructure}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Fee Structure Dialog */}
      <Dialog
        open={showViewFeeModal}
        onClose={() => setShowViewFeeModal(false)}
        PaperProps={{
          className: "dialog-paper",
        }}
      >
        <DialogTitle id="responsive-dialog-title" className="dialog-delete">
          View Fee Structure
        </DialogTitle>
        <DialogContent className="dialog-content">
          {selectedFeeStructure && (
            <>
              <div className="flex-container">
                <strong className="label1">Program Name</strong>
                <span className="value1">
                  {selectedFeeStructure.program_name || "N/A"}
                </span>
              </div>
              <div className="flex-container">
                <strong className="label1">Total Fee</strong>
                <span className="value1">
                  ${selectedFeeStructure.total_fee?.toLocaleString() || "0"}
                </span>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowViewFeeModal(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default FeeStructureV2;
